import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Calendar, 
  Ticket, 
  Users, 
  Tag, 
  Settings, 
  LogOut,
  Menu,
  X,
  Wine,
  Package,
  Globe,
  Image,
  Mail,
  FileText,
  Search,
  Sparkles,
  BarChart3,
  MousePointer,
  FlaskConical,
  Bell,
  UserCheck,
  Code,
  Cake,
  Images,
  Sprout,
  Gift
} from "lucide-react";

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const username = localStorage.getItem("adminUser");
    
    if (!token) {
      navigate("/admin");
      return;
    }
    
    setUser(username);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin");
  };

  const navItems = [
    { path: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/events", icon: Calendar, label: "Events" },
    { path: "/admin/landing-pages", icon: Sparkles, label: "Landing Pages" },
    { path: "/admin/bookings", icon: Ticket, label: "Bookings" },
    { path: "/admin/guest-management", icon: UserCheck, label: "Guest Management" },
    { path: "/admin/guests", icon: Users, label: "Guests (Legacy)" },
    { path: "/admin/messages", icon: Mail, label: "Messages" },
    { path: "/admin/treats-inquiries", icon: Cake, label: "Treats Orders" },
    { path: "/admin/treats-images", icon: Image, label: "Treats Images" },
    { path: "/admin/treats-gallery", icon: Images, label: "Treats Gallery" },
    { path: "/admin/mini-makers", icon: Sprout, label: "Mini Makers Waitlist" },
    { path: "/admin/market-camp-registrations", icon: Sprout, label: "Market Camp Regs" },
    { path: "/admin/market-camp-tickets", icon: Sprout, label: "Market Camp Tickets" },
    { path: "/admin/market-camp-groups", icon: Sprout, label: "Market Camp Groups" },
    { path: "/admin/market-camp-referrals", icon: Gift, label: "Camp Referrals ($)" },
    { path: "/admin/market-camp-ambassadors", icon: Users, label: "Camp Ambassadors" },
    { path: "/admin/market-camp-coupons", icon: Tag, label: "Camp Coupons" },
    { path: "/admin/market-camp-settings", icon: Sprout, label: "Market Camp Settings" },
    { path: "/admin/coupons", icon: Tag, label: "Coupons" },
    { path: "/admin/packages", icon: Package, label: "Packages" },
    { path: "/admin/gallery", icon: Image, label: "Gallery" },
    { path: "/admin/pages", icon: FileText, label: "Page Builder" },
    { path: "/admin/seo", icon: Search, label: "SEO Center" },
    // Analytics Section
    { type: "divider", label: "Analytics" },
    { path: "/admin/conversions", icon: BarChart3, label: "Conversions" },
    { path: "/admin/pixel-settings", icon: Code, label: "Pixel & Tracking" },
    { path: "/admin/ab-tests", icon: FlaskConical, label: "A/B Tests" },
    // System Section
    { type: "divider", label: "System" },
    { path: "/admin/email-settings", icon: Bell, label: "Email Alerts" },
    { path: "/admin/website", icon: Globe, label: "Website" },
    { path: "/admin/settings", icon: Settings, label: "Settings" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0F0F0F] border-r border-[rgba(212,175,55,0.15)] transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
            <Link to="/" className="flex items-center gap-3">
              <Wine className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <span className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F0]">
                  Mix & Mingle
                </span>
                <p className="text-[#a1a1aa] text-xs">Admin Portal</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item, index) => (
                item.type === "divider" ? (
                  <li key={`divider-${index}`} className="pt-4 pb-2">
                    <span className="px-4 text-xs font-semibold text-[#a1a1aa] uppercase tracking-wider">
                      {item.label}
                    </span>
                  </li>
                ) : (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded transition-colors ${
                        isActive(item.path)
                          ? "bg-[#D4AF37] text-[#050505]"
                          : "text-[#A1A1AA] hover:bg-[rgba(212,175,55,0.1)] hover:text-[#D4AF37]"
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                )
              ))}
            </ul>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-[rgba(212,175,55,0.15)]">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
                  <span className="text-[#050505] font-semibold">
                    {user?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-[#F5F5F0] text-sm font-medium">{user}</p>
                  <p className="text-[#a1a1aa] text-xs">Administrator</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-[#0F0F0F] border-b border-[rgba(212,175,55,0.15)] px-6 py-4 flex items-center justify-between lg:justify-end">
          <button
            className="lg:hidden p-2 text-[#A1A1AA]"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          <Link 
            to="/" 
            className="text-[#A1A1AA] hover:text-[#D4AF37] text-sm transition-colors"
            target="_blank"
          >
            View Website →
          </Link>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
